import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { baseRequest } from 'src/app/common/classes/request-builder';
import { FormRequestTypes } from 'src/app/common/classes/form-request';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { Utils } from 'src/app/common/utils';

import { BaseForm } from 'src/app/view/base-form';

import { DashboardService } from 'src/app/service/events/dashboard.service';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import { ProductosApiService } from 'src/app/service/api/productos-api';
import { ClientesApiService } from 'src/app/service/api/clientes-api.service';
import { TratamientosApiService } from 'src/app/service/api/tratamientos-api.service';
import { Filtering } from 'src/app/service/filtering/filtering';

import { ClienteModel } from 'src/app/models/cliente.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { Uso } from 'src/app/models/productos/uso.model';
import { UserModel } from 'src/app/models/usuarios/user.model';
import { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';

import { TratamientosFormDefinition } from './form-control/form-definition';

type CachedProductDetail = {
    usos: Array<{ label: string; value: Uso | null }>;
    agentes: Array<{ label: string; value: Uso | null }>;
};

@Component({
    selector: 'app-tratamientos-form',
    standalone: false,
    templateUrl: './tratamientos-form.component.html',
    styleUrls: ['./tratamientos-form.component.scss']
})
export class TratamientosFormComponent extends BaseForm<TratamientosModel> implements OnInit {
    public form!: TratamientosFormDefinition;

    public loadingData: boolean = true;

    /**
     * Stable snapshot used only to bootstrap the builder.
     * It must NOT be replaced with live form changes, otherwise the child builder
     * receives a new previousData input on every edit and re-patches itself.
     */
    public initialPreviousData: TratamientosModel | null = null;

    /**
     * Last runtime snapshot received from the builder.
     * Used for read operations only; not fed back into [previousData].
     */
    private runtimeModelSnapshot: TratamientosModel | null = null;

    /**
     * Cache MAGRAMA detail by registration number to avoid repeated requests.
     */
    private readonly productDetailCache = new Map<string, CachedProductDetail>();

    private rawClients: ClienteModel[] = [];

    private rawProducts: ProductosModel[] = [];

    public constructor(
        public tratamientosApi: TratamientosApiService,
        public formApi: FormCommonApiService,
        public productosApi: ProductosApiService,
        public clientesApi: ClientesApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public dashboardEvents: DashboardService
    ) {
        super(
            route,
            router,
            dashboardEvents,
            tratamientosApi.tratamientosPUT,
            tratamientosApi.tratamientosPOST,
            'tratamientos',
            'Editar Tratamiento',
            'Crear Tratamiento',
            'Duplicar Tratamiento'
        );
    }

    public ngOnInit(): void {
        this.dropdownsFinalized = false;

        this.form = new TratamientosFormDefinition(
            this.getType(),
            this.getModel() as TratamientosModel,
            null
        );

        this.form.model = {
            ...this.form.model,
            ...(this.getModel() as TratamientosModel)
        };

        this.initialPreviousData = { ...this.form.model };
        this.runtimeModelSnapshot = { ...this.form.model };

        this.form.usosResolver = (producto: ProductosModel | null) => {
            if (!producto || !producto.num_registro || producto.tipo === 'fertilizante') {
                return {
                    values: [{ label: 'Uso...', value: null }],
                    filtered: [{ label: 'Uso...', value: null }],
                    selected: null
                };
            }

            const detail = this.getCachedProductDetail(String(producto.num_registro));

            if (!detail) {
                return {
                    values: [{ label: 'Cargando...', value: null }],
                    filtered: [{ label: 'Cargando...', value: null }],
                    selected: null
                };
            }

            return {
                values: detail.usos,
                filtered: detail.usos,
                selected: null
            };
        };

        this.form.agentesResolver = (producto: ProductosModel | null, uso: Uso | null) => {
            if (!producto || !producto.num_registro || producto.tipo === 'fertilizante') {
                return {
                    values: [{ label: 'Agente...', value: null }],
                    filtered: [{ label: 'Agente...', value: null }],
                    selected: null
                };
            }

            const detail = this.getCachedProductDetail(String(producto.num_registro));

            if (!detail) {
                return {
                    values: [{ label: 'Cargando...', value: null }],
                    filtered: [{ label: 'Cargando...', value: null }],
                    selected: null
                };
            }

            const filteredAgentes = uso
                ? detail.agentes.filter((item) => {
                    return String(item.value?.uso ?? '').trim().toLowerCase()
                        === String(uso.uso ?? '').trim().toLowerCase();
                })
                : detail.agentes;

            const resolvedAgentes = filteredAgentes.length > 0 || !uso
                ? filteredAgentes
                : detail.agentes;

            const deduplicatedAgentes = this.deduplicateOptionsByLabel(resolvedAgentes);
            const values = [{ label: 'Agente...', value: null }, ...deduplicatedAgentes];

            return {
                values,
                filtered: values,
                selected: null
            };
        };

        void this.initFormData();
        this.softInit(this.getType());
    }

    public formChanges(formModel: object, tag: string): void {
        this.runtimeModelSnapshot = { ...(formModel as TratamientosModel) };
        this.form.tag = tag;

        if (tag === 'id_cliente') {
            this.postProcessClientes();
            this.dropdownLoadTrigger.next();
            return;
        }

        if (tag === '__repeatable__' || tag === 'ids_productos') {
            this.postProcessProductos();

            void this.ensureDependentDataLoaded().finally(() => {
                this.dropdownLoadTrigger.next();
            });

            return;
        }

        this.dropdownLoadTrigger.next();
    }

    public getCachedProductDetail(registro: string): CachedProductDetail | null {
        return this.productDetailCache.get(registro) ?? null;
    }

    private normalizePrimaryKey(value: unknown, primaryKey: string = 'id'): string {
        if (value == null) {
            return '';
        }

        if (typeof value === 'object') {
            return String((value as Record<string, unknown>)[primaryKey] ?? '').trim();
        }

        return String(value).trim();
    }

    private getRuntimeModelSnapshot(): TratamientosModel {
        const builderValue = this.formBuilderRef?.model?.getRawValue?.() as TratamientosModel | undefined;

        if (builderValue) {
            return builderValue;
        }

        if (this.runtimeModelSnapshot) {
            return this.runtimeModelSnapshot;
        }

        return this.form.model;
    }

    private mapClientsToOptions(clients: ClienteModel[]): Array<{ label: string; value: ClienteModel }> {
        return clients.map((item) => ({
            label: item.nombre || item.razon_social || '',
            value: item
        }));
    }

    private mapProductsToOptions(products: ProductosModel[]): Array<{ label: string; value: ProductosModel }> {
        return products.map((item) => ({
            label: String(item.nombre ?? '').trim(),
            value: item
        }));
    }


    private normalizeOptionLabel(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private deduplicateOptionsByLabel<T extends { label: string }>(options: T[]): T[] {
        const seenLabels = new Set<string>();

        return options.filter((item) => {
            const normalizedLabel = this.normalizeOptionLabel(item.label);

            if (normalizedLabel === '' || seenLabels.has(normalizedLabel)) {
                return false;
            }

            seenLabels.add(normalizedLabel);
            return true;
        });
    }

    private getSelectedClientId(): string {
        const currentModel = this.getRuntimeModelSnapshot();

        return this.normalizePrimaryKey(currentModel.id_cliente, 'id');
    }

    private getSelectedProductIds(): Set<string> {
        const currentModel = this.getRuntimeModelSnapshot();

        return new Set(
            String(currentModel.ids_productos ?? '')
                .split(';')
                .map((item) => item.trim())
                .filter((item) => item !== '')
        );
    }

    private initFormData(): Promise<void> {
        this.loadingData = true;

        return Promise.all([
            baseRequest(this.clientesApi.clientesGET),
            baseRequest(this.productosApi.productos.GET)
        ]).then(([clientsResponse, productsResponse]) => {
            this.rawClients = Array.isArray(clientsResponse)
                ? (clientsResponse as ClienteModel[]).filter((item): item is ClienteModel => item instanceof Object)
                : [];

            this.rawProducts = Array.isArray(productsResponse)
                ? (productsResponse as ProductosModel[]).filter((item): item is ProductosModel => item instanceof Object)
                : [];

            const clientOptions = this.mapClientsToOptions(this.rawClients);
            const productOptions = this.mapProductsToOptions(this.rawProducts);

            this.form.lists.clientes.filtered = [...clientOptions];
            this.form.lists.clientes.values = [...clientOptions];

            this.form.lists.productosAll.filtered = [...productOptions];
            this.form.lists.productosAll.values = [...productOptions];

            this.postProcessClientes();
            this.postProcessProductos();
            this.applyInitialState();

            this.loadingData = false;
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();

            setTimeout(() => {
                void this.ensureDependentDataLoaded().finally(() => {
                    this.dropdownLoadTrigger.next();
                });
            }, 0);
        }).catch((error: unknown) => {
            console.error('Error loading tratamientos form data:', error);
            this.loadingData = false;
            this.dropdownsFinalized = true;
        });
    }

    private postProcessClientes(): void {
        const user = StorageManager.getUser() as UserModel;

        const source = [...this.rawClients];

        let filteredClients = source;

        if (!this.form.env.showTareasMto) {
            if (!(this.form.env.appName === 'sequiador' && user.rol === 'admin')) {
                filteredClients = source.filter((item) => {
                    return [item.id_usuario_asignado, item.id_usuario_asignado2].includes(user.id);
                });
            }
        }

        const selectedClientId = this.getSelectedClientId();

        if (selectedClientId !== '') {
            const selectedClient = source.find((item) => {
                return String(item.id ?? '') === selectedClientId;
            }) ?? null;

            if (selectedClient && !filteredClients.some((item) => String(item.id ?? '') === selectedClientId)) {
                filteredClients = [selectedClient, ...filteredClients];
            }
        }

        filteredClients = [...filteredClients].sort((a, b) => {
            return String(a.nombre ?? a.razon_social ?? '').localeCompare(
                String(b.nombre ?? b.razon_social ?? '')
            );
        });

        this.form.lists.clientes.filtered = this.mapClientsToOptions(filteredClients);

        this.form.lists.clientes.values = [...this.form.lists.clientes.filtered];

        if (this.getType() === FormRequestTypes.CREATE) {
            const currentClientId = String(StorageManager.getClient()?.id ?? '');

            const currentClient = filteredClients.find((item) => {
                return String(item.id ?? '') === currentClientId;
            }) ?? null;

            if (currentClient) {
                this.form.lists.clientes.selected = currentClient;
                this.form.model.id_cliente = String(currentClient.id ?? '');
                return;
            }
        }

        const selectedClient = filteredClients.find((item) => {
            return String(item.id ?? '') === selectedClientId;
        }) ?? null;

        if (selectedClient) {
            this.form.lists.clientes.selected = selectedClient;

            const clientControl = this.formBuilderRef?.model?.get('id_cliente');
            const currentControlId = this.normalizePrimaryKey(clientControl?.value, 'id');

            if (clientControl && (currentControlId === '' || currentControlId === selectedClientId)) {
                clientControl.patchValue(selectedClient, { emitEvent: false });
            }
        }
    }

    private postProcessProductos(): void {
        const source = [...this.rawProducts];

        const selectedProductIds = this.getSelectedProductIds();

        const filteredProducts = source
            .filter((item) => {
                const productId = String(item.id ?? '');

                if (selectedProductIds.has(productId)) {
                    return true;
                }

                return this.form.env.showTratamientosAllProductos
                    ? true
                    : item.tipo === 'fitosanitario' || item.tipo === 'fertilizante';
            })
            .map((item) => ({
                ...item,
                nombre: String(item.nombre ?? '').trim().toUpperCase()
            }))
            .filter(Filtering.distinctBy.bind({ field: 'id' }))
            .sort((a, b) => String(a.nombre ?? '').localeCompare(String(b.nombre ?? '')));

        this.form.lists.productos.filtered = filteredProducts.map((item) => {
            const level = Utils.checkIfProductIsValid(item.fecha_caducidad, item.tipo ?? '');

            return {
                label: `${level} ${item.nombre}`.trim(),
                value: item
            };
        });

        this.form.lists.productos.values = [...this.form.lists.productos.filtered];
    }

    private applyInitialState(): void {
        if (this.getType() === FormRequestTypes.CREATE) {
            const user = StorageManager.getUser() as UserModel;
            const clientId = user.tipo === 'comunero'
                ? String(user.id_cliente ?? '')
                : String(StorageManager.getClient()?.id ?? '');

            if (clientId !== '') {
                this.form.model.id_cliente = clientId;
            }
        }

        if (this.getType() === FormRequestTypes.DUPLICATE) {
            delete this.form.model.id;
        }
    }

    private async ensureDependentDataLoaded(): Promise<void> {
        const selectedProducts = this.getSelectedProductsFromModel();

        for (const product of selectedProducts) {
            const registro = String(product.num_registro ?? '').trim();

            if (registro === '' || product.tipo === 'fertilizante') {
                continue;
            }

            if (this.productDetailCache.has(registro)) {
                continue;
            }

            await this.fetchProductDetail(product);
        }
    }

    private getSelectedProductsFromModel(): ProductosModel[] {
        const currentModel = this.getRuntimeModelSnapshot();
        const ids = String(currentModel.ids_productos ?? '')
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item !== '');

        if (ids.length === 0) {
            return [];
        }

        const productMap = new Map<string, ProductosModel>(
            this.rawProducts.map((item) => [String(item.id ?? ''), item])
        );

        return ids
            .map((id) => productMap.get(id) ?? null)
            .filter((item): item is ProductosModel => item !== null);
    }

    private fetchProductDetail(product: ProductosModel): Promise<void> {
        const registro = String(product.num_registro ?? '').trim();

        if (registro === '') {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this.formApi.magramaDetalleProducto.response((response: Uso[]) => {
                const seenUsoLabels = new Set<string>();
                const seenAgenteUsageLabels = new Set<string>();

                const usos = (response ?? [])
                    .map((item) => ({
                        label: item.uso ?? '',
                        value: item
                    }))
                    .filter((item) => {
                        const usageLabel = this.normalizeOptionLabel(item.label);

                        if (usageLabel === '' || seenUsoLabels.has(usageLabel)) {
                            return false;
                        }

                        seenUsoLabels.add(usageLabel);
                        return true;
                    });

                const agentes = (response ?? [])
                    .map((item) => {
                        const normalized: Uso = {
                            ...item,
                            minDosis: item.dosis1 ?? 0,
                            maxDosis: item.dosis2 ?? 0,
                            dosis_min: item.dosis1 ?? 0,
                            dosis_max: item.dosis2 ?? 0,
                            isInvalid: false
                        };

                        return {
                            label: normalized.agente ?? '',
                            value: normalized
                        };
                    })
                    .filter((item) => {
                        const usageLabel = this.normalizeOptionLabel(item.value?.uso ?? '');
                        const agentLabel = this.normalizeOptionLabel(item.label);
                        const uniqueKey = `${usageLabel}::${agentLabel}`;

                        if (agentLabel === '' || seenAgenteUsageLabels.has(uniqueKey)) {
                            return false;
                        }

                        seenAgenteUsageLabels.add(uniqueKey);
                        return true;
                    });

                this.productDetailCache.set(registro, {
                    usos: [{ label: 'Uso...', value: null }, ...usos],
                    agentes
                });

                this.formApi.magramaDetalleProducto.unsuscribe();
                resolve();
            });

            this.formApi.magramaDetalleProducto.safePerform({
                referencia: `'${registro}'`
            } as never);
        });
    }
}
